// A job's status as the platform's status dropdown (finance-real, the user 2026-10-22: "status is changed via drop down
// component like the other statuses in the platform"), the pill IdeaStatusBadge and BriefStatusBadge are: StatusBadge,
// which the quote's and the invoice's status use too (DD-062). The user picks Draft, Agreed, Active, Paused, Done or
// Dropped; Quoting and Invoiced show in the menu, disabled, with what sets them, as their quote and their invoice do. What
// the server would refuse from where the job stands is disabled too, its tag saying why, and `lockedReason` says why for
// the drawer's line under it. Draft from Active, Paused or Done stays open with claims on the job: the server refuses it,
// naming them (the user, 2026-10-24). On an accepted quote a job takes any status it allows; Draft takes it off the quote.
import { ENTITY_CONFIG } from "@/core/entityConfig";
import type { LineJob, LineJobPickedStatus, LineJobStatus } from "../types";
import { JOB_STATUS, JOB_STATUSES } from "./lineJobText";
import { StatusBadge } from "./StatusBadge";

const quoteWord = ENTITY_CONFIG.quote.label.toLowerCase();
const invoiceWord = ENTITY_CONFIG.invoice.label.toLowerCase();
// The user's job, a line item, rides main's `contract` key (ENTITY_CONFIG).
const jobWord = ENTITY_CONFIG.contract.label.toLowerCase();

const PICKED: LineJobPickedStatus[] = ["draft", "agreed", "active", "paused", "done", "dropped"];
const isPicked = (s: LineJobStatus): s is LineJobPickedStatus => PICKED.some((p) => p === s);

/** Why the status is not the user's to change here: its proposed quote's, its invoice's, or its draft quote's. Null when
 * it is. */
export function lockedReason(job: LineJob, onOpenQuote: boolean): string | null {
  if (job.status === "quoting") return `Set by its proposed ${quoteWord}: set the ${quoteWord} back to draft, or take it off, to change it.`;
  if (job.status === "invoiced") {
    // Only a draft gives a job back (DELETE /job-invoices/:id/jobs/:jobId); a sent one goes back to draft while nothing
    // has arrived on it (PUT /job-invoices/:id/status), and one money reached holds its jobs.
    switch (job.invoice?.status) {
      case "draft":
        return `Set by its draft ${invoiceWord}: take it off the ${invoiceWord} and it is done again.`;
      case "sent":
        return `Set by its sent ${invoiceWord}: only a draft gives a ${jobWord} back, so set the ${invoiceWord} back to Draft first.`;
      case "overdue":
        return `Set by its overdue ${invoiceWord}: only a draft gives a ${jobWord} back, and one goes back to Draft only while no money has arrived on it.`;
      default:
        return `Set by its ${invoiceWord}: money has arrived on it, so it stays invoiced.`;
    }
  }
  if (onOpenQuote) return `On a draft ${quoteWord}: accepting the ${quoteWord} makes it agreed, to start from here. Dropping it takes it off.`;
  return null;
}

/** Today in Rome, the server's day, as an ISO date. */
const romeToday = (): string => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Rome" });

/** Done when its period ends, and that period is over: set back to Active, the next tick makes it done again. */
const doneByItsDays = (job: LineJob): boolean =>
  job.status === "done" && job.doneWhen === "period_end" && job.endsOn !== null && job.endsOn < romeToday();

/** The statuses the server takes from where the job stands (setJobStatus). Agreed waits with no days, so only a job not
 * started takes it: a draft, or one dropped before it had days. Done follows Active and goes back to it, unless it is
 * done by its days (`doneByItsDays`). */
function allowed(job: LineJob, to: LineJobPickedStatus, onOpenQuote: boolean): boolean {
  if (job.status === "quoting" || job.status === "invoiced") return false;
  if (onOpenQuote) return to === "dropped";
  switch (to) {
    case "draft":
      return job.status !== "draft";
    case "agreed":
      return job.status === "draft" || (job.status === "dropped" && job.startsOn === null);
    case "active":
      return !doneByItsDays(job);
    case "paused":
    case "done":
      return job.status === "active";
    case "dropped":
      return true;
  }
}

/** A closed choice's tag: what holds the job, or what the status needs. */
function whyNot(job: LineJob, to: LineJobPickedStatus, onOpenQuote: boolean): string | undefined {
  if (job.status === "quoting" || onOpenQuote) return `By the ${quoteWord}`;
  if (job.status === "invoiced") return `By the ${invoiceWord}`;
  if (to === "agreed") return "Before it starts";
  if (to === "paused" || to === "done") return "From active";
  if (to === "active" && doneByItsDays(job)) return "Its period ended";
  return undefined;
}

interface Props {
  job: LineJob;
  /** It is on a draft or a proposed quote. */
  onOpenQuote: boolean;
  onPick: (status: LineJobPickedStatus) => void;
  loading?: boolean;
  align?: "left" | "right";
}

export function JobStatusBadge({ job, onOpenQuote, onPick, loading = false, align = "left" }: Props) {
  return (
    <StatusBadge<LineJobStatus>
      value={job.status}
      choices={JOB_STATUSES.map((s) => ({
        value: s,
        look: JOB_STATUS[s],
        setBy: s === "quoting" ? `By the ${quoteWord}` : s === "invoiced" ? `By the ${invoiceWord}` : undefined,
        disabled: !isPicked(s) || !allowed(job, s, onOpenQuote),
        why: isPicked(s) && s !== job.status ? whyNot(job, s, onOpenQuote) : undefined,
      }))}
      onPick={(s) => {
        if (isPicked(s)) onPick(s);
      }}
      loading={loading}
      align={align}
      ariaLabel={`${job.name} status`}
    />
  );
}
